import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export type Notification = {
  id: string
  title: string
  body: string
  image_url: string | null
  link: string | null
  type: string
  sent_count: number
  created_at: string
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(false)

  // Check if notifications are enabled
  useEffect(() => {
    const stored = localStorage.getItem('ca-notifications-enabled')
    if (stored === 'true') {
      setEnabled(true)
      loadNotifications()
    }
  }, [])

  // Load notifications from Supabase
  const loadNotifications = useCallback(async () => {
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20)
    setNotifications(data || [])
  }, [])

  // Subscribe to realtime notifications
  useEffect(() => {
    if (!enabled) return

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          const newNotification = payload.new as Notification
          setNotifications((prev) => [newNotification, ...prev])
          setUnreadCount((prev) => prev + 1)
        }
      )
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [enabled])

  // Enable notifications
  const enableNotifications = useCallback(async () => {
    setLoading(true)
    localStorage.setItem('ca-notifications-enabled', 'true')
    setEnabled(true)
    await loadNotifications()
    setLoading(false)
  }, [loadNotifications])

  // Disable notifications
  const disableNotifications = useCallback(async () => {
    setLoading(true)
    localStorage.removeItem('ca-notifications-enabled')
    setEnabled(false)
    setNotifications([])
    setUnreadCount(0)
    setLoading(false)
  }, [])

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setUnreadCount(0)
    localStorage.setItem('ca-notifications-read', new Date().toISOString())
  }, [])

  // Send notification (admin only)
  const sendNotification = useCallback(async (notification: {
    title: string
    body: string
    image_url?: string
    link?: string
    type?: string
  }) => {
    const { data, error } = await supabase
      .from('notifications')
      .insert({
        title: notification.title,
        body: notification.body,
        image_url: notification.image_url || null,
        link: notification.link || null,
        type: notification.type || 'breaking',
      })
      .select()
      .single()

    if (error) {
      console.error('Error sending notification:', error)
      return null
    }

    // Also call edge function to trigger push
    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (token) {
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-push-notification`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: notification.title,
            body: notification.body,
            image_url: notification.image_url,
            link: notification.link,
            notification_id: data?.id,
          }),
        })
      }
    } catch {
      // Edge function call failed but notification was saved
    }

    return data
  }, [])

  return {
    notifications,
    unreadCount,
    enabled,
    loading,
    enableNotifications,
    disableNotifications,
    markAllAsRead,
    sendNotification,
    loadNotifications,
  }
}